<script lang="ts">
  import { createEventDispatcher, onDestroy } from 'svelte';
  import { derived } from 'svelte/store';
  import { createTimer, getTimerSessionTaskId } from '$lib/domain/timer';
  import type { TimerState } from '$lib/domain/timer';
  import { consumeNotificationEvent } from '$lib/services/notificationEvents';
  import { sendLocalNotification } from '$lib/services/notifications';
  import { activePlan, activeSession, nextScheduledPlan, plansStore } from '$lib/stores/plans';
  import { selectedTask } from '$lib/stores/tasks';
  import { notificationStore } from '$lib/stores/notifications';
  import { getSessionLabel } from '$lib/domain/scheduler';
  import { getNextSessionAction } from '$lib/domain/sessionTransition';
  import type { PlanSession } from '$lib/domain/types';

  export let compact = false;

  const dispatch = createEventDispatcher();

  let appliedSessionId: string | null = null;
  let playedPlanStartFor: string | null = null;
  let transitionInFlight = false;
  const TIMER_SESSION_KEY = 'ff_timer_session_id';
  let timerSessionId =
    typeof localStorage === 'undefined' ? null : localStorage.getItem(TIMER_SESSION_KEY);
  let lastFocusModeState = false;
  let notificationSettings = {
    enabled: true,
    volume: 0.7,
    soundPack: 'aurora',
    playOn: { workComplete: true, breakComplete: true, planStart: true },
  };

  const timer = createTimer(
    1500,
    async (completion) => {
      const session = $activeSession?.id === timerSessionId ? $activeSession : null;
      const metrics = timer.getMetrics();
      if (
        (!$activeSession || session) &&
        !completion.recovered &&
        consumeNotificationEvent(completion)
      ) {
        const eventType =
          session?.type && session.type !== 'work' ? 'breakComplete' : 'workComplete';
        void playEvent(eventType);
        if (
          notificationStore.isLoaded() &&
          notificationSettings.enabled &&
          notificationSettings.playOn[eventType]
        ) {
          void sendLocalNotification(
            eventType === 'workComplete' ? 'Focus complete' : 'Break complete',
            {
              tag: completion.eventId,
              silent: true,
            },
          );
        }
      }
      await completeCurrentSession(metrics, timerSessionId, completion.recovered);
    },
    { storageKey: 'ff_timer_state' },
  );

  let timerState: TimerState = {
    timeLeft: 1500,
    duration: 1500,
    status: 'idle',
    isRunning: false,
    targetTimeMs: null,
    pauseStartedAtMs: null,
    completedAtMs: null,
    recoveredAtMs: null,
    interruptions: 0,
    pausedSeconds: 0,
  };
  const unsubscribe = timer.subscribe((value) => (timerState = value));
  const unsubscribeNotifications = notificationStore.subscribe(
    (value) => (notificationSettings = value),
  );

  const sessionLabel = derived(activeSession, ($session) =>
    $session ? getSessionLabel($session.type) : 'No active plan',
  );

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  const playEvent = async (event: 'workComplete' | 'breakComplete' | 'planStart') => {
    if (
      !notificationStore.isLoaded() ||
      !notificationSettings?.enabled ||
      !notificationSettings.playOn?.[event]
    )
      return;

    const soundMap: Record<string, string> = {
      aurora: '/sounds/aurora-chime.wav?v=2',
      dusk: '/sounds/dusk-bell.wav?v=2',
      halo: '/sounds/halo-soft.wav?v=2',
    };

    const url = soundMap[notificationSettings.soundPack] ?? soundMap.aurora;
    const audio = new Audio(url);
    audio.volume = notificationSettings.volume ?? 0.7;
    await audio.play().catch(() => null);
  };

  const setTimerSessionId = (sessionId: string | null) => {
    timerSessionId = sessionId;
    if (typeof localStorage === 'undefined') return;
    if (sessionId) localStorage.setItem(TIMER_SESSION_KEY, sessionId);
    else localStorage.removeItem(TIMER_SESSION_KEY);
  };

  const captureSessionTask = (session: PlanSession | null) => {
    // Legacy paused/running sessions without captured evidence stay unassigned.
    if (!session || timerState.status !== 'idle') return;
    timer.setSessionContext({
      sessionId: session.id,
      taskId: session.type === 'work' ? ($selectedTask?.id ?? null) : null,
    });
  };

  const startTimer = async () => {
    if (transitionInFlight || timerState.isRunning) return;
    let planStarted = false;
    const freshStart = timerState.status === 'idle' && !$activeSession?.startedAt;
    if (!$activePlan) {
      if (
        (timerState.status === 'paused' || timerState.status === 'interrupted') &&
        !timerSessionId
      ) {
        timer.start();
        return;
      }
      if ($nextScheduledPlan) {
        planStarted = await plansStore.startPlan($nextScheduledPlan.id);
        if (!planStarted) return;
      } else {
        timer.start();
        return;
      }
    }
    await plansStore.markSessionStarted();
    setTimerSessionId($activeSession?.id ?? null);
    if ($activePlan && freshStart && (planStarted || playedPlanStartFor !== $activePlan.id)) {
      playedPlanStartFor = $activePlan.id;
      if (
        consumeNotificationEvent({
          eventId: `plan:${$activePlan.id}:start`,
          scheduledAtMs: Date.now(),
          recovered: false,
        })
      ) {
        void playEvent('planStart');
      }
    }
    captureSessionTask($activeSession);
    timer.start();
  };

  const pause = () => timer.pause();
  const reset = () => timer.reset();

  const endRecoveredSession = () => {
    if (transitionInFlight) return;
    if (timerState.status === 'recovered' && timer.flushRecoveredCompletion()) return;
    void completeCurrentSession(timer.getMetrics(), timerSessionId, true);
  };

  const completeCurrentSession = async (
    metrics = timer.getMetrics(),
    expectedSessionId = timerSessionId,
    recovered = false,
  ) => {
    if (transitionInFlight) return;
    // Restored pages did not observe this plan's original auto-start. Consume
    // that activation before preparing the next block, so the reactive initial
    // auto-start path cannot undo the user's recovery decision.
    if (recovered && $activePlan) autoStartPlanId = $activePlan.id;
    if ($activeSession && $activeSession.id !== expectedSessionId) {
      autoStartPlanId = $activePlan?.id ?? null;
      setTimerSessionId($activeSession.id);
      timer.reset($activeSession.duration * 60);
      return;
    }
    transitionInFlight = true;
    const shouldAutoStart = !recovered && ($activePlan?.autoStart ?? false);

    try {
      const taskIdAtStart = getTimerSessionTaskId(timerState, expectedSessionId);
      const completion = await plansStore.completeSession(
        {},
        metrics,
        taskIdAtStart,
        expectedSessionId,
        timerState.completedAtMs ?? Date.now(),
      );
      if (!completion) {
        const currentSession = $activeSession;
        if (!currentSession) {
          setTimerSessionId(null);
          timer.reset();
          return;
        }

        setTimerSessionId(currentSession.id);
        timer.reset(currentSession.duration * 60);
        if (shouldAutoStart) {
          await plansStore.markSessionStarted();
          captureSessionTask(currentSession);
          timer.start();
        }
        return;
      }
      const nextSession = completion?.nextSession ?? null;
      const action = getNextSessionAction({
        hasNextSession: Boolean(nextSession),
        autoStart: shouldAutoStart,
      });

      if (!nextSession || action === 'stop') {
        setTimerSessionId(null);
        timer.reset();
        return;
      }

      appliedSessionId = nextSession.id;
      setTimerSessionId(nextSession.id);
      timer.reset(nextSession.duration * 60);
      if (action === 'start') {
        await plansStore.markSessionStarted();
        captureSessionTask(nextSession);
        timer.start();
      }
    } finally {
      transitionInFlight = false;
    }
  };

  const handleShortcut = (event: KeyboardEvent) => {
    if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.altKey) return;
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    if (
      target instanceof HTMLInputElement ||
      target instanceof HTMLTextAreaElement ||
      target instanceof HTMLSelectElement ||
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target.isContentEditable ||
      target.closest('[role="dialog"]')
    )
      return;

    if (event.code === 'Space') {
      event.preventDefault();
      if (timerState.status === 'recovered') {
        endRecoveredSession();
      } else if (timerState.isRunning) {
        pause();
      } else {
        void startTimer();
      }
    }

    if (event.code === 'KeyR') {
      event.preventDefault();
      reset();
    }

    if (event.code === 'KeyP') {
      event.preventDefault();
      dispatch('openPlan');
    }
  };

  let autoStartPlanId: string | null = null;
  let progressPercent = '0%';
  let timerTitle = 'Deep Work';
  let timerStatus = 'Ready';
  let startActionLabel = 'Start';
  let sessionsRemaining = 0;
  let progressValue = 0;
  let timerAnnouncement = 'Timer ready.';
  let timerHelp =
    'Press Space to start or pause the timer. Press R to reset. Press P to open planning.';
  let focusModeActive = false;

  $: if (
    $plansStore.loaded &&
    $activeSession &&
    $activeSession.id !== appliedSessionId &&
    !timerState.isRunning &&
    !transitionInFlight &&
    timerState.status !== 'interrupted' &&
    timerState.status !== 'recovered' &&
    timerState.status !== 'completed'
  ) {
    appliedSessionId = $activeSession.id;
    const isRestoredPause = timerSessionId === $activeSession.id && timerState.status === 'paused';
    setTimerSessionId($activeSession.id);
    if (!isRestoredPause) timer.setDuration($activeSession.duration * 60);
  }

  $: if ($plansStore.loaded && !$activeSession && !timerState.isRunning) {
    appliedSessionId = null;
    if (timerState.status !== 'recovered') setTimerSessionId(null);
  }

  $: if (
    $plansStore.loaded &&
    $activePlan?.autoStart &&
    timerState.status === 'idle' &&
    !$activeSession?.startedAt
  ) {
    const due = Date.parse($activePlan.startAt) <= Date.now();
    if (due && autoStartPlanId !== $activePlan.id) {
      autoStartPlanId = $activePlan.id;
      startTimer();
    }
  }

  $: if ($activePlan?.autoStart && timerState.isRunning && autoStartPlanId !== $activePlan.id) {
    autoStartPlanId = $activePlan.id;
  }

  $: {
    const progress = timerState.duration ? 1 - timerState.timeLeft / timerState.duration : 0;
    const clamped = Math.min(Math.max(progress, 0), 1);
    progressValue = Math.round(clamped * 100);
    progressPercent = `${progressValue}%`;
  }

  $: timerTitle = $activePlan?.title ?? $nextScheduledPlan?.title ?? 'Deep Work';
  $: timerStatus = timerState.isRunning
    ? 'In progress'
    : timerState.status === 'paused'
      ? 'Paused'
      : timerState.status === 'interrupted'
        ? 'Resume when ready'
        : timerState.status === 'recovered'
          ? 'Recovered'
          : timerState.status === 'completed'
            ? 'Complete'
            : 'Ready';
  $: timerAnnouncement = timerState.isRunning
    ? `${$sessionLabel} running.`
    : timerState.status === 'paused'
      ? `${$sessionLabel} paused.`
      : timerState.status === 'recovered'
        ? `${$sessionLabel} ended while you were away. End the session when ready; unobserved time is not focus credit.`
        : timerState.status === 'interrupted'
          ? 'Timer interrupted. Resume or end the session; unobserved time is not focus credit.'
          : timerState.status === 'completed'
            ? `${$sessionLabel} complete.`
            : `${$sessionLabel}. Timer is idle.`;
  $: startActionLabel =
    timerState.status === 'paused' || timerState.status === 'interrupted'
      ? 'Resume'
      : 'Start Focus';
  $: sessionsRemaining = $activePlan
    ? Math.max(0, ($plansStore.sessions?.length ?? 0) - ($plansStore.activeSessionIndex ?? 0))
    : 0;
  $: focusModeActive = timerState.isRunning;
  $: plansStore.setTimerBusy(
    timerState.isRunning ||
      timerState.status === 'paused' ||
      timerState.status === 'recovered' ||
      timerState.status === 'interrupted' ||
      transitionInFlight,
  );
  $: if (focusModeActive !== lastFocusModeState) {
    lastFocusModeState = focusModeActive;
    dispatch('focusChange', {
      active: focusModeActive,
      running: timerState.isRunning,
      status: timerState.status,
    });
  }

  $: if (typeof document !== 'undefined') {
    document.title = `${formatTime(timerState.timeLeft)} ${timerState.isRunning ? 'running' : 'ready'} - Focus Flow`;
  }

  onDestroy(() => {
    unsubscribe();
    unsubscribeNotifications();
    timer.destroy();
    plansStore.setTimerBusy(false);
    if (typeof document !== 'undefined') document.title = 'Focus Flow';
  });
</script>

<svelte:window on:keydown={handleShortcut} />

<div
  id="timer-tabpanel"
  class="glass-panel timer-panel focus-stage"
  class:is-compact={compact}
  class:is-running={timerState.isRunning}
  class:is-paused={timerState.status === 'paused'}
  class:is-recovered={timerState.status === 'recovered'}
  role="tabpanel"
  aria-labelledby="timer-title"
  data-testid="timer-panel"
>
  <div class="panel-header">
    <div>
      <p class="kicker">{$activeSession ? $sessionLabel : 'One thing at a time'}</p>
      <h2 id="timer-title"><bdi>{timerTitle}</bdi></h2>
    </div>
  </div>

  {#if timerState.status === 'recovered' || timerState.status === 'interrupted'}
    <div class="timer-topic" role="status" aria-live="polite" aria-atomic="true">
      <span data-testid="recovery-note">
        {timerState.recoveryReason === 'clock-change'
          ? 'The clock changed.'
          : 'The timer was interrupted.'}
        Unobserved time will not count as focus. {timerState.timeLeft > 0
          ? 'Resume or end this session.'
          : 'End this session when ready.'}
      </span>
    </div>
  {/if}

  {#if $selectedTask}
    <div class="timer-linked-task" data-testid="timer-linked-task">
      <span class="task-dot"></span>
      <div>
        <strong>Selected task</strong>
        <span
          ><bdi>{$selectedTask.title}</bdi>{#if $selectedTask.project}
            · <bdi>{$selectedTask.project}</bdi>{/if}</span
        >
      </div>
      <button class="text-button" on:click={() => dispatch('chooseTask')}>Change</button>
    </div>
  {:else}
    <button class="choose-task text-button" on:click={() => dispatch('chooseTask')}
      >Choose a task <span aria-hidden="true">↗</span></button
    >
  {/if}

  <p id="timer-help" class="sr-only">{timerHelp}</p>
  <p class="sr-only" role="status" aria-live="polite" aria-atomic="true">{timerAnnouncement}</p>

  <div class="timer-hero" aria-label="Focus timer">
    <div class="timer-readout">
      <div class="timer-display" role="timer" aria-live="off" dir="ltr" data-testid="timer-time">
        {formatTime(timerState.timeLeft)}
      </div>
      <div class="timer-subtext" data-testid="timer-status">{timerStatus}</div>
      <div
        class="timer-progress"
        style:--progress={progressPercent}
        role="progressbar"
        aria-labelledby="timer-title"
        aria-describedby="timer-help"
        aria-valuemin="0"
        aria-valuemax="100"
        aria-valuenow={progressValue}
        aria-valuetext={`${progressValue}% complete. ${formatTime(timerState.timeLeft)} remaining.`}
      >
        <span></span>
      </div>
    </div>

    <div class="timer-controls" aria-label="Timer controls">
      {#if timerState.status === 'recovered'}
        <button class="button timer-primary" on:click={endRecoveredSession}>End session</button>
      {:else if !timerState.isRunning}
        <button class="button timer-primary" aria-keyshortcuts="Space" on:click={startTimer}>
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M8 5.5v13l10-6.5-10-6.5Z" />
          </svg>
          {startActionLabel}
        </button>
      {:else}
        <button class="button secondary timer-primary" aria-keyshortcuts="Space" on:click={pause}>
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M8 5v14M16 5v14" />
          </svg>
          Pause
        </button>
      {/if}
      {#if timerState.status === 'interrupted'}
        <button class="button ghost" on:click={endRecoveredSession}>End session</button>
      {:else if timerState.status !== 'idle'}
        <button class="button ghost" aria-keyshortcuts="R" on:click={reset}>
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M20 12a8 8 0 1 1-2.34-5.66" />
            <path d="M20 4v6h-6" />
          </svg>
          Reset
        </button>
      {/if}
    </div>
  </div>

  <div class="timer-secondary">
    {#if compact}
      <button class="text-button" on:click={() => dispatch('showTimer')}>Back to timer</button>
    {:else}
      <button class="text-button" aria-keyshortcuts="P" on:click={() => dispatch('openPlan')}
        >Schedule Plan</button
      >
      {#if $activePlan}<span>{$sessionLabel} · {sessionsRemaining} sessions left</span>{/if}
      <span class="shortcut-hint"
        ><kbd>space</kbd> to {timerState.isRunning ? 'pause' : 'start'}</span
      >
    {/if}
  </div>
</div>
